import React, { useState, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { DownOutlined } from "@ant-design/icons";
import { CompassParagraph } from "../styledComponents";
import {
  Icon,
  IconContainer,
  GradientText,
} from "../../visuals/styledComponents";
import { GradientProgressBar } from "../FileUpload/GradientProgressBar";
import { ElementContainer } from "../../containers/styledComponents";
import {
  compassLightGreen,
  greyText,
  AI_GRADIENT,
  lightGreys,
} from "../../../styles";

import aiIcon from "../../../assets/images/ai_logo_2.png";
import fileIcon from "../../../assets/svg/file_icon.svg";
import fileIconGrey from "../../../assets/svg/file_icon_grey.svg";
import fileUploadIcon from "../../../assets/svg/file_upload.svg";

// Components
import { DeleteItem } from "../../tables/DeleteItem";
import { CompassAIButton } from "../../buttons/styledComponents";

// =============================================================================
// ANIMATIONS (only slide - matching app patterns)
// =============================================================================

const popDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  60% {
    opacity: 1;
    transform: translateY(4px) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: scaleY(0.8);
  }
  to {
    opacity: 1;
    transform: scaleY(1);
  }
`;

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const Container = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }): string => ($isVisible ? "block" : "none")};
  margin-bottom: 16px;
  animation: ${popDown} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  transform-origin: top center;
`;

const Wrapper = styled.div`
  position: relative;
  background: white;
  border-radius: 8px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 8px;
    padding: 1px;
    background: linear-gradient(135deg, #6ef2a6, #32e0c4, #2bb3f3, #3a5cf5);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0.6;
    pointer-events: none;
  }
`;

const Header = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  cursor: pointer;
  border-radius: ${({ $isExpanded }): string =>
    $isExpanded ? "8px 8px 0 0" : "8px"};
  transition: all 0.2s ease;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChevronIcon = styled(DownOutlined)<{ $isExpanded: boolean }>`
  font-size: 12px;
  color: #c6c6c6;
  transition: transform 0.2s ease;
  transform: ${({ $isExpanded }): string =>
    $isExpanded ? "rotate(180deg)" : "rotate(0)"};
`;

const HeaderProgress = styled.div`
  flex: 1;
  max-width: 150px;
  margin-left: 10px;
`;

const AnimatedDots = styled.span`
  display: inline-flex;
  margin-left: 1px;

  span {
    background: ${AI_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${dotPulse} 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

const Content = styled.div`
  padding: 14px;
  border-top: 1px solid rgba(32, 226, 144, 0.15);
  animation: ${slideDown} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top center;
`;

// Drop zone - matching FileDropZone.tsx patterns exactly
const DropZone = styled.div<{ $isDragOver: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  width: 100%;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: ${({ $isDragOver }): string =>
    $isDragOver ? "rgba(0, 100, 80, 0.02)" : "white"};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const DropZoneIconWrapper = styled(IconContainer)<{ $isDragOver: boolean }>`
  transition: transform 0.2s ease-in-out;
  ${({ $isDragOver }): string => ($isDragOver ? "transform: scale(1.1);" : "")}
  &:hover {
    transform: scale(1.1);
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
  pointer-events: none;
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

// File card - slim single-line style
const FileCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: ${lightGreys.KPIBackground};
  border-radius: 12px;
  padding: 6px 10px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const FileName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.span`
  font-size: 11px;
  color: ${greyText};
  flex-shrink: 0;
`;

const FileStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ProgressWrapper = styled.div`
  width: 50px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e8e8e8;
  }
`;

const TextInput = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Apercu", sans-serif;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(32, 226, 144, 0.5);
  }

  &::placeholder {
    color: #bbb;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

// =============================================================================
// TYPES
// =============================================================================

/** Uploaded file state (shared between engagement and interaction prefill hooks) */
export interface UploadedFileState {
  file: File;
  fileKey: string | null;
  isUploading: boolean;
  isUploaded: boolean;
  error: string | null;
}

export interface InlinePrefillBaseProps {
  /** Whether the prefill is visible */
  isVisible: boolean;
  /** Extracting label text (shown in header during extraction) */
  extractingLabel: string;
  /** Text area placeholder */
  textPlaceholder: string;
  /** Callback when extract is triggered — returns a Promise resolving to the result (or null on failure) */
  onExtract: (prompt: string) => Promise<unknown | null>;
  /** Hook-provided state */
  uploadedFiles: UploadedFileState[];
  addFile: (file: File) => Promise<void>;
  removeFile: (index: number) => void;
  reset: () => void;
  isUploading: boolean;
  isExtracting: boolean;
  canAddMoreFiles: boolean;
  /** Custom class name */
  className?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_PROMPT_LENGTH = 50000;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Base inline prefill UI component.
 * Renders the collapsible Smart Fill card with file drop zone, text area, and extract button.
 * Decoupled from the specific prefill hook so it can be used for both engagement and interaction prefill.
 */
export const InlinePrefillBase: React.FC<InlinePrefillBaseProps> = ({
  isVisible,
  extractingLabel,
  textPlaceholder,
  onExtract,
  uploadedFiles,
  addFile,
  removeFile,
  reset,
  isUploading,
  isExtracting,
  canAddMoreFiles,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [promptText, setPromptText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isProcessing = isUploading || isExtracting;
  const hasContent =
    uploadedFiles.some((f) => f.isUploaded) || promptText.trim().length > 0;
  const canExtract = hasContent && !isProcessing;

  const handleToggle = useCallback((): void => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleClose = useCallback((): void => {
    reset();
    setPromptText("");
    setIsExpanded(false);
  }, [reset]);

  const handleFileSelect = useCallback(
    async (file: File): Promise<void> => {
      await addFile(file);
    },
    [addFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((): void => {
    setIsDragOver(false);
  }, []);

  const handleExtract = useCallback(async (): Promise<void> => {
    if (!canExtract) return;

    const result = await onExtract(promptText);
    if (result) {
      handleClose();
    }
  }, [canExtract, onExtract, promptText, handleClose]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Container $isVisible={isVisible} className={className}>
      <Wrapper>
        <Header
          $isExpanded={isExpanded && !isExtracting}
          onClick={isExtracting ? undefined : handleToggle}
          style={{ cursor: isExtracting ? "default" : "pointer" }}
        >
          <HeaderLeft>
            <IconContainer size={38} m={-1} p={-1} br={50}>
              <Icon src={aiIcon} alt="AI" />
            </IconContainer>
            <GradientText style={{ fontSize: "13px", fontWeight: 700 }}>
              {isExtracting ? (
                <>
                  {extractingLabel}
                  <AnimatedDots>
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </AnimatedDots>
                </>
              ) : (
                "Smart Fill"
              )}
            </GradientText>
            {isExtracting && (
              <HeaderProgress>
                <GradientProgressBar isLoading={true} />
              </HeaderProgress>
            )}
          </HeaderLeft>
          {!isExtracting && (
            <ChevronIcon $isExpanded={isExpanded} color="#dadada" />
          )}
        </Header>

        {isExpanded && !isExtracting && (
          <Content>
            {/* Drop zone - matching FileDropZone.tsx exactly */}
            {canAddMoreFiles && (
              <DropZone
                $isDragOver={isDragOver}
                onClick={(): void => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <DropZoneIconWrapper size={70} m={0} $isDragOver={isDragOver}>
                  <Icon src={fileUploadIcon} alt="Upload file" />
                </DropZoneIconWrapper>
                <ElementContainer mt={8}>
                  <CompassParagraph
                    color={compassLightGreen}
                    mr={5}
                    bold
                    mb={0}
                  >
                    Click to upload
                  </CompassParagraph>
                  <CompassParagraph mr={5} mb={0}>
                    or drag and drop
                  </CompassParagraph>
                </ElementContainer>
                <CompassParagraph fontSize={12} color={greyText} mt={8} mb={0}>
                  PDF, DOCX, XLSX, CSV, TXT (Max. 25MB each)
                </CompassParagraph>
                <HiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.xlsx,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                  onChange={handleInputChange}
                />
              </DropZone>
            )}

            {/* Uploaded files - below drop zone */}
            {uploadedFiles.length > 0 && (
              <FilesList style={{ marginTop: canAddMoreFiles ? 12 : 0 }}>
                {uploadedFiles.map((file, index) => (
                  <FileCard key={`${file.file.name}-${index}`}>
                    <FileInfo>
                      <IconContainer size={32} m={1} p={1}>
                        <Icon
                          src={file.isUploading ? fileIconGrey : fileIcon}
                          alt="file"
                        />
                      </IconContainer>
                      <FileName>{file.file.name}</FileName>
                      <FileSize>{formatFileSize(file.file.size)}</FileSize>
                    </FileInfo>
                    <FileStatus>
                      {file.isUploading ? (
                        <ProgressWrapper>
                          <GradientProgressBar isLoading={true} />
                        </ProgressWrapper>
                      ) : file.error ? (
                        <CompassParagraph fontSize={11} color="#ff4d4f" mb={0}>
                          Failed
                        </CompassParagraph>
                      ) : null}
                      <span
                        style={{
                          visibility: file.isUploading ? "hidden" : "visible",
                        }}
                      >
                        <DeleteItem
                          id={index}
                          isDeletable={true}
                          onDelete={(): void => removeFile(index)}
                        />
                      </span>
                    </FileStatus>
                  </FileCard>
                ))}
              </FilesList>
            )}

            <Divider>
              <CompassParagraph fontSize={11} color={greyText} mb={0} mr={0}>
                OR
              </CompassParagraph>
            </Divider>

            <TextInput
              value={promptText}
              onChange={(e): void => setPromptText(e.target.value)}
              placeholder={textPlaceholder}
              maxLength={MAX_PROMPT_LENGTH}
            />
            <CompassParagraph
              fontSize={11}
              color={
                promptText.length > MAX_PROMPT_LENGTH * 0.9
                  ? "#ff4d4f"
                  : greyText
              }
              ta="right"
              mt={4}
              mb={0}
              style={{
                visibility: promptText.length > 0 ? "visible" : "hidden",
              }}
            >
              {promptText.length.toLocaleString()} /{" "}
              {MAX_PROMPT_LENGTH.toLocaleString()}
            </CompassParagraph>

            <Footer>
              <CompassAIButton
                onClick={handleExtract}
                disabled={!canExtract || isProcessing}
                size="s"
                $padding="8px 24px"
              >
                <span>Extract Fields</span>
              </CompassAIButton>
            </Footer>
          </Content>
        )}
      </Wrapper>
    </Container>
  );
};
